import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Form, Input, InputNumber, DatePicker, Select, useApp, Alert, Typography, Button } from '../../ui';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { FileUploadField } from '../../components/FileUploadField';

type PresetOrder = { id: string; orderNo: string; retailerId: string; grandTotal: number | string; orderedQty?: number; pendingQty?: number };
/** Per-shipment (partial) invoicing: bills ONE dispatch. Carries the order context + the shipment id. */
type PresetShipment = { id: string; shipmentNo: string; orderId: string; orderNo: string; retailerId: string };

type Props = {
  open: boolean;
  onClose: () => void;
  /** When opened from an order: locks retailer/order and prefills the total. */
  presetOrder?: PresetOrder | null;
  /** When opened for a specific dispatch (PER_SHIPMENT order): bills that shipment, prefills the target. */
  presetShipment?: PresetShipment | null;
};

/**
 * Record a tax invoice produced in Tally: its number/date/total + the uploaded PDF.
 * The backend posts the retailer ledger and (when an order is linked) marks it INVOICED.
 */
export function InvoiceUploadModal({ open, onClose, presetOrder, presetShipment }: Props) {
  const { message } = useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const canViewRetailers = usePermission('RETAILER.VIEW');
  const [retailerId, setRetailerId] = useState<string | undefined>();
  const [pdfUrl, setPdfUrl] = useState<string>('');

  // Per-shipment: the target totals this dispatch's Tally invoice should match (the guardrail).
  const { data: target } = useQuery({
    queryKey: ['shipment-invoice-target', presetShipment?.id],
    queryFn: () => request<any>({ url: `/shipments/${presetShipment!.id}/invoice-target` }),
    enabled: open && !!presetShipment,
  });

  // Reset + seed the form ONCE when the modal opens (or the preset changes). Deliberately excludes
  // `target`: the shipment-invoice-target query resolves after the modal is interactive, and re-running
  // resetFields()/setPdfUrl('') then would wipe a typed invoice number / uploaded PDF.
  useEffect(() => {
    if (open) {
      setPdfUrl('');
      setRetailerId(presetShipment?.retailerId ?? presetOrder?.retailerId);
      form.resetFields();
      form.setFieldsValue({
        invoiceDate: dayjs(),
        grandTotal: presetShipment ? undefined : presetOrder ? Number(presetOrder.grandTotal) : undefined,
        orderId: presetShipment?.orderId ?? presetOrder?.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, presetOrder, presetShipment, form]);

  // When the per-shipment target arrives, seed ONLY the grand-total guardrail — no reset, so nothing
  // the user has typed or uploaded is disturbed.
  useEffect(() => {
    if (open && presetShipment && target?.grand != null) {
      form.setFieldsValue({ grandTotal: Number(target.grand) });
    }
  }, [open, presetShipment, target, form]);

  // Retailer picker (standalone use only).
  const { data: retailers } = useQuery({
    queryKey: ['invoice-upload-retailers'],
    queryFn: () => request<any>({ url: '/retailers', params: { pageSize: 1000 } }),
    enabled: open && !presetOrder && canViewRetailers,
  });

  // DELIVERED orders for the chosen retailer (standalone use), to link the invoice.
  const { data: orders } = useQuery({
    queryKey: ['invoice-upload-orders', retailerId],
    queryFn: () => request<any>({ url: '/orders', params: { retailerId, status: 'DELIVERED', pageSize: 100 } }),
    enabled: open && !presetOrder && !!retailerId,
  });

  const save = useMutation({
    mutationFn: (vals: any) =>
      request({
        url: '/invoices',
        method: 'POST',
        data: {
          invoiceNo: String(vals.invoiceNo).trim(),
          invoiceDate: vals.invoiceDate.format('YYYY-MM-DD'),
          dueDate: vals.dueDate ? vals.dueDate.format('YYYY-MM-DD') : undefined,
          grandTotal: Number(vals.grandTotal),
          pdfUrl,
          orderId: presetShipment?.orderId ?? presetOrder?.id ?? vals.orderId ?? undefined,
          shipmentId: presetShipment?.id ?? undefined,
          retailerId: (presetOrder || presetShipment) ? undefined : retailerId,
        },
      }),
    onSuccess: () => {
      message.success('Tally invoice recorded');
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['outstanding-aging'] });
      const oid = presetShipment?.orderId ?? presetOrder?.id;
      if (oid) {
        qc.invalidateQueries({ queryKey: ['order', oid] });
        qc.invalidateQueries({ queryKey: ['order-invoices', oid] });
        qc.invalidateQueries({ queryKey: ['order-timeline', oid] });
        qc.invalidateQueries({ queryKey: ['order-shipments', oid] });
      }
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to record invoice'),
  });

  const submit = () => {
    if (!pdfUrl) { message.error('Upload the Tally invoice PDF'); return; }
    if (!presetOrder && !presetShipment && !retailerId) { message.error('Select a retailer'); return; }
    form.submit();
  };
  const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  // Whole-order invoice on a partially-packed order → warn: only part is packed, don't over-bill.
  const packedQty = presetOrder ? Math.max(0, Number(presetOrder.orderedQty ?? 0) - Number(presetOrder.pendingQty ?? 0)) : 0;
  const partialPacked = !!presetOrder && Number(presetOrder.pendingQty ?? 0) > 0.001 && packedQty > 0;

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose() }}
      title={presetShipment ? `Upload Tally invoice — shipment ${presetShipment.shipmentNo}` : presetOrder ? `Upload Tally invoice — order ${presetOrder.orderNo}` : 'Upload Tally invoice'}
      width={620}
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={save.isPending} onClick={submit}>Save invoice</Button>
      </>}
    >
      {presetShipment ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Invoice ONLY this shipment — not the whole order."
          description={target
            ? <span>Bill the packed quantity in this shipment. <b>Target: {inr(target.grand)}</b> (taxable {inr(target.taxable)} + GST {inr(Number(target.cgst) + Number(target.sgst) + Number(target.igst))}). A different total will be flagged as a mismatch and block dispatch.</span>
            : 'Bill only the packed quantity in this shipment; the whole order is invoiced across its shipments.'}
        />
      ) : (
        <>
          {partialPacked && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`Only ${packedQty} of ${presetOrder!.orderedQty} units are packed — ${presetOrder!.pendingQty} still pending.`}
              description="If you're dispatching only what's packed, bill just those units in Tally. The grand total below defaults to the whole order — adjust it to match what you're actually invoicing. (New orders use per-shipment invoicing, which bills each dispatch automatically.)"
            />
          )}
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Record the tax invoice raised in Tally."
            description="Enter the Tally invoice number, date and grand total, and attach the PDF. This posts the retailer's outstanding; for an order awaiting dispatch, it unlocks dispatch once recorded."
          />
        </>
      )}
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        {presetShipment ? (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
            Linked to shipment <b>{presetShipment.shipmentNo}</b> of order <b>{presetShipment.orderNo}</b>.
          </Typography.Paragraph>
        ) : presetOrder ? (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
            Linked to order <b>{presetOrder.orderNo}</b>.
          </Typography.Paragraph>
        ) : (
          <>
            <Form.Item label="Retailer" required>
              <Select
                placeholder="Select retailer"
                showSearch
                optionFilterProp="label"
                value={retailerId}
                onChange={(v) => { setRetailerId(v); form.setFieldValue('orderId', undefined); }}
                options={(retailers?.data ?? []).map((r: any) => ({
                  value: String(r.id),
                  label: `${r.companyName}${r.city ? ` · ${r.city}` : ''}`,
                }))}
              />
            </Form.Item>
            <Form.Item name="orderId" label="Link to delivered order (optional)">
              <Select
                placeholder={retailerId ? 'Select a delivered order' : 'Pick a retailer first'}
                allowClear
                disabled={!retailerId}
                onChange={(_v) => {
                  const o = (orders?.data ?? []).find((x: any) => String(x.id) === String(_v));
                  if (o) form.setFieldValue('grandTotal', Number(o.grandTotal));
                }}
                options={(orders?.data ?? []).map((o: any) => ({
                  value: String(o.id),
                  label: `${o.orderNo} · ₹${Number(o.grandTotal).toLocaleString('en-IN')}`,
                }))}
              />
            </Form.Item>
          </>
        )}

        <Form.Item name="invoiceNo" label="Tally invoice number" rules={[{ required: true, message: 'Required' }]}>
          <Input placeholder="e.g. GB/2526/001234" />
        </Form.Item>
        <Form.Item name="invoiceDate" label="Invoice date" rules={[{ required: true, message: 'Required' }]}>
          <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" disabledDate={(d) => d.isAfter(dayjs().endOf('day'))} />
        </Form.Item>
        <Form.Item name="dueDate" label="Due date (optional — defaults to credit period)">
          <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
        </Form.Item>
        <Form.Item name="grandTotal" label="Grand total (₹)" rules={[{ required: true, message: 'Required' }]}>
          <InputNumber style={{ width: '100%' }} min={1} step={1} precision={2} />
        </Form.Item>
        <Form.Item label="Tally invoice PDF" required>
          <FileUploadField category="invoice" buttonLabel="Upload invoice PDF" value={pdfUrl} onChange={setPdfUrl} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
